import { defineChatActions } from './actionsContext'
import { checkAuth } from './runtime'
import {
  resolveMatrixMessageTarget,
} from './messageMerge'
import { debugConsoleError } from '@/modules/chat-matrix-fast/utils/chatDebugLog'
import { logger } from '@/utils/eventLogger'
import type {
  IMessage,
  IPendingReactionOverride,
  IReactionType,
} from '@/modules/chat-matrix-fast/types/IChat'
import {
  useCurrentUser,
} from '@/modules/chat-matrix-fast/composables/useCurrentUser'
import {
  getCurrentMatrixUserId,
} from '@/modules/chat-matrix-fast/services/matrixChatClient'
import { getMatrixClient } from '@/modules/chat-matrix-fast/services/matrixClientLifecycle'
import {
  applyPendingReactionOverride,
  enrichMessagesWithReactions,
  enrichMessagesWithReactionsAsync,
  reactionTypeIdToEmoji,
  removeMyMatrixReactions,
  sendMatrixReaction,
  setMatrixExclusiveReaction,
} from '@/modules/chat-matrix-fast/services/matrixReactionService'
import { getMessageIdentityKey } from '@/modules/chat-matrix-fast/utils/messageIdentity'
import { stripEmojiKeyPrefix } from '@/modules/chat-matrix-fast/utils/reactionUtils'
import {
  enrichMessagesWithReadReceipts,
} from '@/modules/chat-matrix-fast/services/matrixReadReceiptService'

import {
  notifyViewSyncReactions,
} from '@/modules/chat-matrix-fast/stores/chatStoreSync'

const clearingReactionEventIds = new Set<string>()

export const reactionActions = defineChatActions({
  async fetchReactionTypes(): Promise<void> {
    if (!checkAuth()) {
      return
    }

    // Если уже загружены, не делаем повторный запрос
    if (this.reactionTypesLoaded && this.reactionTypes.length > 0) {
      return
    }

    // Пытаемся загрузить из localStorage
    try {
      const cachedTypes = localStorage.getItem('chat_reaction_types')
      if (cachedTypes) {
        const parsedTypes = JSON.parse(cachedTypes) as IReactionType[]
        if (Array.isArray(parsedTypes) && parsedTypes.length > 0) {
          this.reactionTypes = parsedTypes
          this.reactionTypesLoaded = true

          return // Используем кешированные типы
        }
      }
    } catch (error) {
      // Игнорируем ошибки чтения из localStorage
    }

    this.reactionTypes = [
      { id: 1, name: 'Like', icon: '👍' },
      { id: 2, name: 'Love', icon: '❤️' },
      { id: 3, name: 'Laugh', icon: '😂' },
      { id: 4, name: 'Wow', icon: '😮' },
      { id: 5, name: 'Sad', icon: '😢' },
      { id: 6, name: 'Angry', icon: '😠' },
    ] as IReactionType[]
    this.reactionTypesLoaded = true

    try {
      localStorage.setItem('chat_reaction_types', JSON.stringify(this.reactionTypes))
    } catch {
      // Игнорируем ошибки записи в localStorage
    }
  },

  getPendingReactionOverride(
    message: Pick<IMessage, 'id' | 'matrix_event_id' | 'matrix_txn_id'>,
  ): IPendingReactionOverride | undefined {
    const identityKey = getMessageIdentityKey(message)
    return this.pendingReactionOverrides[identityKey]
        || this.pendingReactionOverrides[String(message.id)]
        || (message.matrix_event_id
          ? this.pendingReactionOverrides[String(message.matrix_event_id)]
          : undefined)
  },

  enrichMessagesWithMatrixReactions(
    messages: IMessage[],
    options?: { applyPending?: boolean; roomId?: string },
  ): IMessage[] {
    const applyPending = options?.applyPending !== false
    const withPending = (message: IMessage): IMessage => (
      applyPending
        ? applyPendingReactionOverride(message, this.getPendingReactionOverride(message))
        : message
    )

    const roomId = options?.roomId || this.activeMessageRoomId
    if (!roomId) {
      return messages.map(withPending)
    }

    try {
      const matrixClient = getMatrixClient()
      const room = matrixClient.getRoom(roomId)
      if (!room) {
        return messages.map(withPending)
      }

      return enrichMessagesWithReadReceipts(
        matrixClient,
        room,
        enrichMessagesWithReactions(matrixClient, room, messages, this.reactionTypes),
      ).map(withPending)
    } catch {
      return messages.map(withPending)
    }
  },

  /** На открытии чата — догружаем реакции с homeserver (/relations). */
  async enrichMessagesWithMatrixReactionsAsync(
    messages: IMessage[],
    options?: { applyPending?: boolean; roomId?: string },
  ): Promise<IMessage[]> {
    const applyPending = options?.applyPending !== false
    const withPending = (message: IMessage): IMessage => (
      applyPending
        ? applyPendingReactionOverride(message, this.getPendingReactionOverride(message))
        : message
    )

    const roomId = options?.roomId || this.activeMessageRoomId
    if (!roomId) {
      return messages.map(withPending)
    }

    try {
      const matrixClient = getMatrixClient()
      const room = matrixClient.getRoom(roomId)
      if (!room) {
        return messages.map(withPending)
      }

      const withReactions = await enrichMessagesWithReactionsAsync(
        matrixClient,
        room,
        messages,
        this.reactionTypes,
      )

      return enrichMessagesWithReadReceipts(
        matrixClient,
        room,
        withReactions,
      ).map(withPending)
    } catch {
      return this.enrichMessagesWithMatrixReactions(messages, options)
    }
  },

  setPendingReactionOverride(
    messageId: number | string,
    override: IPendingReactionOverride,
    matrixEventId?: string,
    txnId?: string,
  ): void {
    const next = {
      ...this.pendingReactionOverrides,
      [String(messageId)]: override,
    }
    if (matrixEventId) {
      next[String(matrixEventId)] = override
    }
    if (txnId) {
      next[`txn:${txnId}`] = override
    }
    this.pendingReactionOverrides = next
  },

  clearPendingReactionOverride(
    messageId: number | string,
    matrixEventId?: string,
  ): void {
    const keys = [String(messageId), matrixEventId ? String(matrixEventId) : '']
      .filter(Boolean)
    if (!keys.some(key => this.pendingReactionOverrides[key])) {
      return
    }

    const rest = { ...this.pendingReactionOverrides }
    for (const key of keys) {
      delete rest[key]
    }
    this.pendingReactionOverrides = rest
  },

  syncReactionsToViewStore(messageId?: number | string): void {
    notifyViewSyncReactions(messageId)
  },

  async addReaction(
    messageId: number | string,
    reactionId: number | string,
    matrixEventIdHint?: string,
  ): Promise<void> {
    if (!checkAuth()) {
      return
    }

    if (!this.currentChat) {
      return
    }

    const currentUserInfo = useCurrentUser(this.currentChat)
    const matrixUserId = String(currentUserInfo.matrixUserId.value ?? '')
    const currentUserName = String(currentUserInfo.nameForChat.value ?? currentUserInfo.name.value ?? '')
    const emoji = stripEmojiKeyPrefix(reactionTypeIdToEmoji(reactionId, this.reactionTypes))
    const numericReactionId = Number(reactionId)

    const target = resolveMatrixMessageTarget(this.messages, messageId, {
      matrixEventIdHint,
      activeMessageRoomId: this.activeMessageRoomId,
      activeTopicRoomId: this.activeTopic?.matrix_room_id,
      currentChatRoomId: this.currentChat.matrix_room_id,
    })

    if (!target || !matrixUserId) {
      throw new Error('Не удалось определить сообщение для реакции')
    }

    const { messageIndex, matrixEventId, roomId } = target

    let originalMessage: any = null
    if (messageIndex !== -1) {
      const currentMessage = this.messages[messageIndex]
      originalMessage = JSON.parse(JSON.stringify(currentMessage))
      const reactions = (currentMessage.reactions || currentMessage.message_reactions || [])
        .filter((r: any) => {
          const reactionUserId = String(r.user || r.user_id || '')
          return reactionUserId !== matrixUserId
        })

      reactions.push({
        id: -Date.now(),
        reaction_type: Number.isFinite(numericReactionId) ? numericReactionId : 0,
        reaction_type_id: Number.isFinite(numericReactionId) ? numericReactionId : undefined,
        matrix_emoji_key: emoji,
        user: matrixUserId,
        user_id: matrixUserId,
        user_name: currentUserName,
        created_at: new Date().toISOString(),
      })

      this.messages.splice(messageIndex, 1, {
        ...currentMessage,
        reactions,
        message_reactions: reactions,
        reaction_updated_at: new Date().toISOString(),
      })

      this.syncReactionsToViewStore(messageId)
    }

    try {
      const matrixClient = getMatrixClient()
      const room = matrixClient.getRoom(roomId)
      if (!room) {
        throw new Error('Chat room not found')
      }

      await sendMatrixReaction(matrixClient, roomId, matrixEventId, emoji)
    } catch (error) {
      debugConsoleError(error)
      logger.error('chat_addReaction_error', {
        file: 'chatStore',
        function: 'addReaction',
        condition: String(error),
      })

      if (originalMessage && messageIndex !== -1) {
        this.messages.splice(messageIndex, 1, originalMessage)
      }

      await this.fetchMessages(this.currentChat.id)
      throw error
    }
  },

  async removeReaction(messageId: number): Promise<void> {
    await this.clearMyReactions(messageId)
  },

  // Устанавливает эксклюзивную реакцию (удаляет старую и добавляет новую)
  async setExclusiveReaction(
    messageId: number | string,
    reactionId: number | string,
    emojiOverride?: string,
    matrixEventIdHint?: string,
  ): Promise<void> {
    if (!this.currentChat) {
      return
    }

    const currentUserInfo = useCurrentUser(this.currentChat)
    const matrixUserId = getCurrentMatrixUserId()
        || String(currentUserInfo.matrixUserId.value ?? '')
    const currentUserName = String(currentUserInfo.nameForChat.value ?? currentUserInfo.name.value ?? '')
    const emoji = stripEmojiKeyPrefix(
      emojiOverride || reactionTypeIdToEmoji(reactionId, this.reactionTypes),
    )
    const numericReactionId = Number(reactionId)

    const target = resolveMatrixMessageTarget(this.messages, messageId, {
      matrixEventIdHint,
      activeMessageRoomId: this.activeMessageRoomId,
      activeTopicRoomId: this.activeTopic?.matrix_room_id,
      currentChatRoomId: this.currentChat.matrix_room_id,
    })

    if (!target || !matrixUserId) {
      throw new Error('Не удалось определить сообщение для реакции')
    }

    const { messageIndex, matrixEventId, roomId } = target
    const resolvedMessageId = getMessageIdentityKey(target.message)

    const previousMyReaction = (target.message.reactions || target.message.message_reactions || [])
      .find((reaction: any) => String(reaction.user || reaction.user_id) === matrixUserId)

    const optimisticReaction = {
      id: -Date.now(),
      reaction_type: Number.isFinite(numericReactionId) ? numericReactionId : 0,
      reaction_type_id: Number.isFinite(numericReactionId) ? numericReactionId : undefined,
      matrix_emoji_key: emoji,
      user: matrixUserId,
      user_id: matrixUserId,
      user_name: previousMyReaction?.user_name || currentUserName,
      avatar: previousMyReaction?.avatar ?? null,
      created_at: new Date().toISOString(),
    }

    this.setPendingReactionOverride(resolvedMessageId, {
      matrixUserId,
      reactions: [optimisticReaction],
      expectedEmoji: emoji,
      updatedAt: new Date().toISOString(),
    }, matrixEventId, target.message.matrix_txn_id)

    let originalMessage: any = null
    originalMessage = JSON.parse(JSON.stringify(target.message))

    const reactions = (target.message.reactions || target.message.message_reactions || [])
      .filter((r: any) => {
        const reactionUserId = String(r.user || r.user_id || '')
        return reactionUserId !== matrixUserId
      })

    reactions.push(optimisticReaction)

    this.messages.splice(messageIndex, 1, {
      ...target.message,
      reactions,
      message_reactions: reactions,
      reaction_updated_at: new Date().toISOString(),
    })

    this.syncReactionsToViewStore(resolvedMessageId)

    try {
      const matrixClient = getMatrixClient()
      const room = matrixClient.getRoom(roomId)
      if (!room) {
        throw new Error('Chat room not found')
      }

      await setMatrixExclusiveReaction(
        matrixClient,
        room,
        matrixEventId,
        emoji,
        matrixUserId,
      )
    } catch (error) {
      debugConsoleError(error)
      this.clearPendingReactionOverride(resolvedMessageId, matrixEventId)

      if (originalMessage) {
        this.messages.splice(messageIndex, 1, originalMessage)
        this.syncReactionsToViewStore(resolvedMessageId)
      }

      throw error
    }
  },

  // Очищает все мои реакции с сообщения
  async clearMyReactions(
    messageId: number | string,
    matrixEventIdHint?: string,
  ): Promise<void> {
    if (!checkAuth()) {
      return
    }

    if (!this.currentChat) {
      return
    }

    const currentUserInfo = useCurrentUser(this.currentChat)
    const matrixUserId = getCurrentMatrixUserId()
        || String(currentUserInfo.matrixUserId.value ?? '')

    const target = resolveMatrixMessageTarget(this.messages, messageId, {
      matrixEventIdHint,
      activeMessageRoomId: this.activeMessageRoomId,
      activeTopicRoomId: this.activeTopic?.matrix_room_id,
      currentChatRoomId: this.currentChat.matrix_room_id,
    })

    if (!target || !matrixUserId) {
      return
    }

    const { messageIndex, matrixEventId, roomId } = target
    const resolvedMessageId = getMessageIdentityKey(target.message)

    if (clearingReactionEventIds.has(matrixEventId)) {
      return
    }
    clearingReactionEventIds.add(matrixEventId)

    this.setPendingReactionOverride(resolvedMessageId, {
      matrixUserId,
      reactions: [],
      expectedEmoji: null,
      updatedAt: new Date().toISOString(),
    }, matrixEventId, target.message.matrix_txn_id)

    let originalMessage: any = null
    originalMessage = JSON.parse(JSON.stringify(target.message))
    const reactions = (target.message.reactions || target.message.message_reactions || [])
      .filter((r: any) => {
        const reactionUserId = String(r.user || r.user_id || '')
        return reactionUserId !== matrixUserId
      })

    this.messages.splice(messageIndex, 1, {
      ...target.message,
      reactions,
      message_reactions: reactions,
      reaction_updated_at: new Date().toISOString(),
    })

    this.syncReactionsToViewStore(resolvedMessageId)

    try {
      const matrixClient = getMatrixClient()
      const room = matrixClient.getRoom(roomId)
      if (!room) {
        throw new Error('Chat room not found')
      }

      await removeMyMatrixReactions(matrixClient, room, matrixEventId, matrixUserId)
    } catch (error) {
      debugConsoleError(error)
      this.clearPendingReactionOverride(resolvedMessageId, matrixEventId)

      if (originalMessage) {
        this.messages.splice(messageIndex, 1, originalMessage)
        this.syncReactionsToViewStore(resolvedMessageId)
      }

      await this.fetchMessages(this.currentChat.id)
    } finally {
      clearingReactionEventIds.delete(matrixEventId)
    }
  },
})
